import clsx from "clsx";

import { STATUS_STYLE } from "@/lib/format";
import { useT, type Chave } from "@/lib/i18n";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect } from "react";

export function Button({
  variant = "primary",
  size = "md",
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "racing" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
}) {
  return (
    <button
      className={clsx(
        "inline-flex items-center justify-center gap-2 rounded-[2px] font-display font-semibold",
        "uppercase tracking-[0.12em] transition disabled:cursor-not-allowed disabled:opacity-45",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nitro-500",
        size === "sm" && "px-3 py-2 text-[11px]",
        size === "md" && "px-4 py-2.5 text-xs",
        size === "lg" && "px-5 py-3.5 text-xs",
        // O gradiente vertical existe para o botão ter a curva de uma
        // peça pintada em vez do chapado de retângulo colorido.
        variant === "primary" &&
          "bg-gradient-to-b from-nitro-400 to-nitro-500 text-carbon-900 hover:to-nitro-600",
        // Ignição: só no que dispara a ação principal da tela.
        variant === "racing" &&
          "bg-gradient-to-b from-racing-400 to-racing-500 text-white hover:to-racing-600",
        variant === "secondary" &&
          "border border-line text-steel hover:border-steel-dim hover:bg-carbon-700",
        variant === "ghost" && "text-steel-muted hover:bg-carbon-700 hover:text-steel",
        variant === "danger" &&
          "border border-racing-600/60 text-racing-400 hover:bg-racing-600/15",
        className,
      )}
      {...props}
    />
  );
}

/* Campo "submerso" na superfície: fundo mais escuro que o card e foco em
   azul de nitro, do jeito que um mostrador acende. */
const FIELD = [
  "w-full rounded-[2px] border bg-carbon-900 px-3 py-2.5 text-steel outline-none transition",
  "placeholder:text-steel-dim focus:border-nitro-500",
].join(" ");

export function Input({
  label,
  error,
  hint,
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  error?: string;
  hint?: string;
}) {
  return (
    <label className="block">
      {label && <span className="eyebrow mb-1.5 block text-steel-dim">{label}</span>}
      <input
        className={clsx(FIELD, error ? "border-red-800" : "border-line", className)}
        {...props}
      />
      {hint && !error && (
        <span className="mt-1 block text-xs text-steel-dim">{hint}</span>
      )}
      {error && <span className="mt-1 block text-sm text-red-400">{error}</span>}
    </label>
  );
}

export function Select({
  label,
  className,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & { label?: string }) {
  return (
    <label className="block">
      {label && <span className="eyebrow mb-1.5 block text-steel-dim">{label}</span>}
      <select className={clsx(FIELD, "border-line", className)} {...props}>
        {children}
      </select>
    </label>
  );
}

/** Checkbox com rótulo e explicação — o formato de toggle do painel.
 *
 * O `hint` não é enfeite: em regra de agenda, o dono precisa saber o que
 * muda para a casa ANTES de clicar, não depois de um cliente reclamar.
 */
export function Switch({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        className="mt-0.5 size-4 accent-[var(--color-nitro-500)]"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="block text-sm font-medium text-steel">{label}</span>
        {hint && <span className="block text-xs text-steel-dim">{hint}</span>}
      </span>
    </label>
  );
}

/** Bloco de campos dentro de um formulário longo. */
export function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card className="p-5">
      <div className="mb-4">
        <h2 className="font-display text-lg font-bold">{title}</h2>
        {description && <p className="text-sm text-steel-muted">{description}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </Card>
  );
}

export function Card({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx("rounded-[2px] border border-line bg-carbon-800", className)}
      {...props}
    />
  );
}

export function Badge({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={clsx(
        // Etiqueta estampada, não pílula: 2px de canto é o que dá o ar de
        // plaqueta de metal em vez de tag de blog.
        "inline-flex items-center rounded-[2px] border px-2 py-0.5 font-display",
        "text-[11px] font-semibold tracking-[0.05em] uppercase",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function PageTitle({
  eyebrow,
  title,
  subtitle,
  action,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        {eyebrow && <p className="eyebrow mb-1.5">{eyebrow}</p>}
        <h1 className="text-2xl font-bold lg:text-3xl">{title}</h1>
        {subtitle && (
          <p className="mt-1 text-sm text-steel-muted first-letter:uppercase">
            {subtitle}
          </p>
        )}
      </div>
      {action}
    </div>
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
}) {
  const t = useT();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 sm:items-center sm:p-4">
      {/* No celular vira bottom sheet: é como o técnico usa entre um serviço e outro. */}
      <div
        className={clsx(
          "max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl border border-line bg-carbon-800",
          "shadow-2xl sm:rounded-[2px]",
          wide ? "sm:max-w-2xl" : "sm:max-w-md",
        )}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-carbon-800 px-5 py-4">
          <h2 className="text-lg font-bold">{title}</h2>
          <button
            onClick={onClose}
            aria-label={t("common.close")}
            className="rounded p-1 text-steel-dim transition hover:bg-carbon-700 hover:text-steel"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  const t = useT();
  return (
    <div
      className={clsx(
        "size-5 animate-spin rounded-full border-2 border-line border-t-nitro-500",
        className,
      )}
      role="status"
      aria-label={t("common.loading")}
    />
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      {icon && <div className="mb-4 text-line">{icon}</div>}
      <h3 className="font-display text-lg font-bold">{title}</h3>
      {description && (
        <p className="mt-1.5 max-w-sm text-sm text-steel-muted">{description}</p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/** Faixa de erro de formulário.
 *
 * Passe também o `code` do erro quando tiver: limite de plano estourado
 * não é defeito do usuário, é oportunidade de venda (a regra está em
 * `apps/billing/middleware.py`), e sai em dourado com um caminho para
 * fazer upgrade — não em vermelho de "você errou".
 */
export function ErrorBanner({ message, code }: { message?: string; code?: string }) {
  const t = useT();
  if (!message) return null;

  // Azul de nitro, não vermelho: limite de plano não é defeito do usuário
  // — é oportunidade de venda, e vermelho lê como "você errou".
  if (code === "PLAN_LIMIT_REACHED") {
    return (
      <div className="rounded-[2px] border border-nitro-600/40 bg-nitro-500/10 px-4 py-3 text-sm text-nitro-400">
        {message}{" "}
        {/* Leva para a tela de planos, não para um e-mail: limite estourado
            é oportunidade de venda (docs/07-billing.md §1), e a venda que
            depende de alguém responder um e-mail amanhã não acontece. */}
        <a className="font-semibold underline underline-offset-2" href="/assinatura">
          {t("boot.seePlans")}
        </a>
      </div>
    );
  }

  return (
    <div className="rounded-[2px] border border-racing-600/60 bg-racing-600/15 px-4 py-3 text-sm text-racing-400">
      {message}
    </div>
  );
}

export function InfoBanner({
  children,
  tone = "nitro",
}: {
  children: ReactNode;
  tone?: "nitro" | "neutral";
}) {
  return (
    <div
      className={clsx(
        "rounded-[2px] border px-4 py-3 text-sm",
        tone === "nitro"
          ? "border-nitro-600/40 bg-nitro-500/10 text-nitro-400"
          : "border-line bg-carbon-800 text-steel-muted",
      )}
    >
      {children}
    </div>
  );
}

/** O selo de estado do agendamento — cor e rótulo no mesmo lugar.
 *
 * Existia espalhado em três telas, cada uma lendo `STATUS_STYLE.label`.
 * Com o rótulo traduzido, três cópias seriam três chances de uma delas
 * ficar em português na tela em inglês.
 */
export function StatusBadge({ status }: { status: string }) {
  const t = useT();
  const chave = `status.${status}` as Chave;
  return <Badge className={STATUS_STYLE[status] ?? ""}>{t(chave)}</Badge>;
}
